import { useEffect, useMemo } from 'react'
import { toast } from 'react-toastify'

import { Layout } from '@loadsmart/loadsmart-ui'

import { RequestStatus } from 'core/domain/Request'
import { AsyncContent } from 'core/ui/async-content/AsyncContent'
import { LoadingState } from 'core/ui/async-content/LoadingState'
import { SignUpBanner } from 'core/ui/components/sign-up-banner/SignUpBanner'
import { useAbortController } from 'core/ui/hooks/useAbortController'
import { MainContent } from 'core/ui/MainContent'

import { user } from 'legacy/utils/user'

import { getExpiredRates, getPendingRates, getSubmittedRates } from '../../domain/BulkSpotQuote'
import { type DraftQuote } from '../../domain/DraftQuote'
import { useAccessorialsHistory } from '../../hooks/useAccessorialsHistory'
import { useBulkSpotQuote } from '../../hooks/useBulkSpotQuote'
import { EmptySpotQuoteState } from '../EmptySpotQuoteState'
import { SpotQuotePageHeader } from '../SpotQuotePageHeader'
import { BulkSpotQuoteHeader } from './BulkSpotQuoteHeader'
import { BulkSpotQuoteInstructionsBanner } from './BulkSpotQuoteInstructionsBanner'
import { BulkSpotQuoteTabs } from './BulkSpotQuoteTabs'

export type BulkSpotQuotePageProps = {
  match: {
    params: {
      id: string
    }
  }
}

export function BulkSpotQuotePage({ match }: BulkSpotQuotePageProps) {
  const { id } = match.params

  const {
    bulkSpotQuoteStatus,
    bulkSpotQuote,
    submitRatesStatus,

    fetchBulkSpotQuote,
    refetchBulkSpotQuote,
    submitRates,
  } = useBulkSpotQuote()

  const isSubmitting = submitRatesStatus === RequestStatus.Pending
  const isSubmitted = submitRatesStatus === RequestStatus.Done
  const shouldDisplaySignUpBanner = isSubmitted && !user.isLoggedIn()

  const pendingRates = useMemo(() => getPendingRates(bulkSpotQuote), [bulkSpotQuote])
  const submittedRates = useMemo(() => getSubmittedRates(bulkSpotQuote), [bulkSpotQuote])
  const expiredRates = useMemo(() => getExpiredRates(bulkSpotQuote), [bulkSpotQuote])

  const { accessorialsHistory, fetchAccessorialsHistory } = useAccessorialsHistory()
  const abortController = useAbortController()

  useEffect(() => {
    fetchBulkSpotQuote(id, abortController)

    return () => {
      abortController.abort()
    }
  }, [id, fetchBulkSpotQuote, abortController])

  useEffect(() => {
    if (bulkSpotQuote?.carrier) {
      fetchAccessorialsHistory(bulkSpotQuote.carrier.id, abortController)

      return () => {
        abortController.abort()
      }
    }
  }, [bulkSpotQuote, fetchAccessorialsHistory, abortController])

  async function handleSubmitRates(rates: Array<DraftQuote>) {
    if (isSubmitting || rates.length === 0) return

    try {
      await submitRates({
        inviteUuid: id,
        rates: rates.map(rate => {
          const { uuid, flatRate, notes, accessorials } = rate

          return {
            id: uuid!,
            flatRate: flatRate!,
            notes,
            accessorials: accessorials,
          }
        }),
      })

      toast.success('Bids submitted successfully')

      refetchBulkSpotQuote(id)
    } catch (error) {
      let errorMessage = 'There was an error saving the rates'
      if (error instanceof Error) {
        errorMessage = error.message
      }
      toast.error(errorMessage)
    }
  }

  return (
    <MainContent>
      <Layout.Stack>
        <AsyncContent
          status={bulkSpotQuoteStatus}
          loadingState={<LoadingState />}
          errorState={<EmptySpotQuoteState kind="bulk" />}
        >
          {bulkSpotQuote && (
            <Layout.Stack space="xl">
              <SpotQuotePageHeader
                shipper={bulkSpotQuote.shipper}
                carrier={bulkSpotQuote.carrier}
              />
              <BulkSpotQuoteInstructionsBanner />
              {shouldDisplaySignUpBanner && <SignUpBanner />}
              <BulkSpotQuoteHeader
                shipper={bulkSpotQuote.shipper}
                totalRatesCount={bulkSpotQuote.rates.length}
                submittedRatesCount={submittedRates.length}
              />
              <BulkSpotQuoteTabs
                id={id}
                submittedRates={submittedRates}
                pendingRates={pendingRates}
                expiredRates={expiredRates}
                disabled={isSubmitting}
                onSubmit={handleSubmitRates}
                accessorialsHistory={accessorialsHistory}
              />
            </Layout.Stack>
          )}
        </AsyncContent>
      </Layout.Stack>
    </MainContent>
  )
}
